import { Cloud, Sun, CloudRain, Wind, Droplets, Eye, Thermometer, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { config } from '@/lib/config';

interface WeatherData {
  location: string;
  temperature: number;
  condition: string;
  humidity: number;
  windSpeed: number;
  visibility: number;
  icon: 'sunny' | 'cloudy' | 'rainy' | 'windy';
}

interface WeatherAPIResponse {
  location: {
    name: string;
    region: string;
    country: string;
  };
  current: {
    temp_f: number;
    condition: {
      text: string;
      code: number;
    };
    humidity: number;
    wind_mph: number;
    vis_miles: number;
  };
}

// WeatherAPI condition codes to icon mapping
const getWeatherIconFromCode = (code: number): WeatherData['icon'] => {
  if (code === 1000) return 'sunny'; // Clear/Sunny
  if ([1003, 1006, 1009].includes(code)) return 'cloudy'; // Partly cloudy, cloudy, overcast
  if ([1063, 1180, 1183, 1186, 1189, 1192, 1195, 1198, 1201, 1240, 1243, 1246].includes(code)) return 'rainy'; // Rain variations
  if ([1087, 1273, 1276, 1279, 1282].includes(code)) return 'rainy'; // Thunderstorms
  return 'cloudy'; // Default fallback
};

const getWeatherIcon = (condition: WeatherData['icon']) => {
  switch (condition) {
    case 'sunny':
      return <Sun className="h-5 w-5 text-yellow-500" />;
    case 'cloudy':
      return <Cloud className="h-5 w-5 text-gray-500" />;
    case 'rainy':
      return <CloudRain className="h-5 w-5 text-blue-500" />;
    case 'windy':
      return <Wind className="h-5 w-5 text-gray-600" />;
    default:
      return <Sun className="h-5 w-5 text-yellow-500" />;
  }
};

// WeatherAPI key from environment variables
const WEATHER_API_KEY = config.weatherApiKey;

const fetchWeatherData = async (): Promise<WeatherData> => {
  if (!WEATHER_API_KEY) {
    throw new Error('WeatherAPI key not configured');
  }

  const response = await fetch(
    `https://api.weatherapi.com/v1/current.json?key=${WEATHER_API_KEY}&q=auto:ip&aqi=yes`
  );

  if (!response.ok) {
    throw new Error(`Weather API request failed: ${response.status}`);
  }

  const data: WeatherAPIResponse = await response.json();

  return {
    location: `${data.location.name}, ${data.location.region}`,
    temperature: data.current.temp_f,
    condition: data.current.condition.text,
    humidity: data.current.humidity,
    windSpeed: data.current.wind_mph,
    visibility: data.current.vis_miles,
    icon: getWeatherIconFromCode(data.current.condition.code),
  };
};

export const CompactWeatherCard: React.FC = () => {
  const { data: weather, isLoading, isError } = useQuery({
    queryKey: ['weather'],
    queryFn: fetchWeatherData,
    staleTime: 10 * 60 * 1000,       // 10 minutes — weather doesn't change fast
    gcTime: 30 * 60 * 1000,           // 30 minutes
    refetchInterval: 10 * 60 * 1000,  // poll every 10 minutes
    retry: 1,
    enabled: !!WEATHER_API_KEY,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (isError || !weather) {
    return (
      <div className="flex items-center">
        <span className="text-xs text-muted-foreground">Weather unavailable</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {/* Temperature & Weather Icon */}
      <div className="flex items-center gap-2">
        {getWeatherIcon(weather.icon)}
        <div className="flex flex-col">
          <div className="text-sm font-semibold leading-none">
            {Math.round(weather.temperature)}°F
          </div>
          <div className="text-xs text-muted-foreground leading-none mt-0.5">
            {weather.condition}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="h-6 w-px bg-border" />

      {/* Compact Stats */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <Droplets className="h-3.5 w-3.5 text-blue-500" />
          <span className="text-xs font-medium">{weather.humidity}%</span>
        </div>

        <div className="flex items-center gap-1">
          <Wind className="h-3.5 w-3.5 text-gray-500" />
          <span className="text-xs font-medium">{Math.round(weather.windSpeed)} mph</span>
        </div>

        <div className="flex items-center gap-1">
          <Eye className="h-3.5 w-3.5 text-green-500" />
          <span className="text-xs font-medium">{weather.visibility} mi</span>
        </div>

        <div className="flex items-center gap-1">
          <Thermometer className="h-3.5 w-3.5 text-red-500" />
          <span className="text-xs font-medium">Good</span>
        </div>
      </div>
    </div>
  );
};
